import type { AutocompleteSearch } from '@/types/Autocomplete'
import type { Address } from '@common/types/Address'
import type { Area } from '@common/types/Area'
import type { AutocompleteResult } from '@common/types/AutocompleteResult'
import type { Boundary } from '@common/types/Boundary'
import type { ConfigSearchFilter } from '@common/types/config/Search'
import type { PartialLine } from '@common/types/Line'
import type { Poi } from '@common/types/Poi'
import { apiCall } from './apiCall'
import type { ModuleObject } from './modules'

export async function fetchSuggestions(
	inputValue: string,
	types: AutocompleteSearch[],
	poiCategories?: string[],
): Promise<AutocompleteResult[]> {
	if (!inputValue.trim()) return []
	try {
		const results = await apiCall<AutocompleteResult[]>('/autocomplete', {
			method: 'GET',
			params: {
				'query': inputValue,
				'poi:category': poiCategories
					? poiCategories.join(',')
					: undefined,
				'types': types.join(','),
			},
		})
		return results
	} catch (err) {
		console.error(err)
		return []
	}
}

export const getSuggestion = (
	suggestion: AutocompleteResult,
): Area | Poi | Address | PartialLine | Boundary => {
	switch (suggestion.type) {
		case 'address':
			return suggestion.address
		case 'area':
			return suggestion.area
		case 'poi':
			return suggestion.poi
		case 'boundary':
			return suggestion.boundary
		default:
			return suggestion.line
	}
}

export const getSuggestionLabel = (suggestion: AutocompleteResult): string => {
	switch (suggestion.type) {
		case 'address':
			return suggestion.address.long_name
		case 'area':
			return suggestion.area.name
		case 'poi':
			return suggestion.poi.name
		case 'boundary':
			return suggestion.boundary.name
		default:
			return suggestion.line.code
	}
}

export function matchSearchRules<
	T extends { filter: Partial<Omit<ConfigSearchFilter, 'def'>> },
>(
	result: AutocompleteResult & { def: string },
	rules: T[],
	currentModule: ModuleObject,
): T | null
export function matchSearchRules<
	T extends { filter: Partial<ConfigSearchFilter> },
>(result: AutocompleteResult, rules: T[], currentModule: ModuleObject): T | null
export function matchSearchRules<
	T extends { filter: Partial<ConfigSearchFilter> },
>(
	result: AutocompleteResult & { def?: string },
	rules: T[],
	currentModule: ModuleObject,
): T | null {
	return (
		rules.find(rule => matchSearchRule(result, rule, currentModule)) ?? null
	)
}

export function matchSearchRule<
	T extends { filter: Partial<Omit<ConfigSearchFilter, 'def'>> },
>(
	result: AutocompleteResult & { def: string },
	rules: T,
	currentModule: ModuleObject,
): boolean
export function matchSearchRule<
	T extends { filter: Partial<ConfigSearchFilter> },
>(result: AutocompleteResult, rules: T, currentModule: ModuleObject): boolean
export function matchSearchRule<
	T extends { filter: Partial<ConfigSearchFilter> },
>(
	result: AutocompleteResult & { def?: string },
	{ filter }: T,
	currentModule: ModuleObject,
): boolean {
	if (filter.mode && !filter.mode.includes(currentModule.mode)) return false
	if (filter.type && !filter.type.includes(result.type)) return false
	if (
		filter['poi:category'] &&
		(result.type !== 'poi' ||
			!filter['poi:category'].includes(result.poi.category))
	)
		return false
	if (filter.def && (!result.def || !filter.def.includes(result.def)))
		return false
	return true
}

export const getPosition = (): Promise<GeolocationPosition> => {
	return new Promise((resolve, reject) => {
		navigator.geolocation.getCurrentPosition(resolve, reject)
	})
}
